import { useCallback, useState } from 'react';
import { travelAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import type { EventTravel, SaveMyRideRequest, SetPlayerTravelRequest } from '../../types/travel';
import { PlayerTravelMode } from '../../types/travel';
import { getErrorMessage } from '../../utils/errorMessage';

/** Hányan ülnek a saját autómban. */
export const aboardMyCar = (travel: EventTravel) => {
  const car = travel.vehicles.find((v) => v.isMine);
  return car ? travel.passengers.filter((p) => !p.goesDirectly && p.travelVehicleId === car.id) : [];
};

/** A saját autó adatai mentéshez, a meglévő gyülekezőponttal együtt. */
export const myRideRequest = (travel: EventTravel, seats: number): SaveMyRideRequest | null => {
  const car = travel.vehicles.find((v) => v.isMine);
  if (!car) return null;
  return {
    seats,
    meetingPoint: car.stops[0]?.name ?? null,
    meetTime: car.stops[0]?.meetTime ?? null,
    notes: car.notes ?? null,
  };
};

/**
 * A szülő / játékos utazási műveletei egy eseményen: játékos beállítása, a
 * saját autó felajánlása, és a "Tudok vinni valakit" jelzés (ha még nincs autó,
 * előbb a felajánlás nyílik meg, és mentés után a gyerek rögtön beül).
 */
export const useTravelResponder = (
  travel: EventTravel | null,
  setTravel: (travel: EventTravel) => void,
  onChanged?: () => void
) => {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  // Akinek a "Tudok vinni valakit"-et jelölték, de még nincs autó
  const [pendingCarFor, setPendingCarFor] = useState<number | null>(null);

  const run = useCallback(
    async (action: () => Promise<EventTravel>, success?: string) => {
      setBusy(true);
      try {
        const updated = await action();
        setTravel(updated);
        onChanged?.();
        if (success) showToast(success);
        return updated;
      } catch (err: any) {
        showToast(getErrorMessage(err, 'A mentés nem sikerült.'), 'error');
        return null;
      } finally {
        setBusy(false);
      }
    },
    [setTravel, onChanged, showToast]
  );

  /**
   * Ha a saját autóból kiszállt az utolsó utas is, az autó lekerül: autót csak
   * az ajánl fel, aki maga is megy vele.
   */
  const dropEmptyCar = useCallback(
    async (updated: EventTravel) => {
      if (!updated.vehicles.some((v) => v.isMine) || aboardMyCar(updated).length > 0) return;
      const result = await run(() => travelAPI.deleteMyRide(updated.eventId));
      if (result) showToast('Az autód lekerült az utazásról, mert már senki nem utazik benne.');
    },
    [run, showToast]
  );

  /** keepCar: egy utas kivétele az autómból ne vonja vissza az üres autót. */
  const setPlayer = useCallback(
    async (playerId: number, request: SetPlayerTravelRequest, keepCar = false) => {
      if (!travel) return;
      setPendingCarFor(null);
      const wasInMyCar = aboardMyCar(travel).some((p) => p.playerId === playerId);
      const updated = await run(() => travelAPI.setPlayerTravel(travel.eventId, playerId, request));
      if (updated && wasInMyCar && !keepCar) await dropEmptyCar(updated);
    },
    [travel, run, dropEmptyCar]
  );

  /** "Tudok vinni valakit": beül a saját autóba (ha tele, eggyel több hely lesz). */
  const chooseCar = useCallback(
    async (playerId: number) => {
      if (!travel) return;
      const car = travel.vehicles.find((v) => v.isMine);
      if (!car) {
        setPendingCarFor(playerId);
        return;
      }
      let current = travel;
      if (aboardMyCar(current).length >= car.seats) {
        const request = myRideRequest(current, car.seats + 1)!;
        const enlarged = await run(() => travelAPI.saveMyRide(current.eventId, request));
        if (!enlarged) return;
        current = enlarged;
      }
      await run(() =>
        travelAPI.setPlayerTravel(current.eventId, playerId, { mode: PlayerTravelMode.Vehicle, travelVehicleId: car.id })
      );
    },
    [travel, run]
  );

  /** Az autó mentése; ha a felajánlás egy gyerek miatt nyílt meg, ő rögtön be is ül. */
  const saveCar = useCallback(
    async (request: SaveMyRideRequest) => {
      if (!travel) return false;
      const isNew = !travel.vehicles.some((v) => v.isMine);
      const saved = await run(
        () => travelAPI.saveMyRide(travel.eventId, request),
        isNew ? 'Köszönjük! Az autód felkerült az utazáshoz.' : 'Az autód adatai elmentve.'
      );
      if (!saved) return false;
      const car = saved.vehicles.find((v) => v.isMine);
      if (pendingCarFor != null && car) {
        const playerId = pendingCarFor;
        setPendingCarFor(null);
        await run(() =>
          travelAPI.setPlayerTravel(saved.eventId, playerId, { mode: PlayerTravelMode.Vehicle, travelVehicleId: car.id })
        );
      }
      return true;
    },
    [travel, run, pendingCarFor]
  );

  const deleteCar = useCallback(async () => {
    if (!travel) return;
    await run(() => travelAPI.deleteMyRide(travel.eventId), 'Az autód lekerült az utazásról.');
  }, [travel, run]);

  return {
    busy,
    pendingCarFor,
    cancelPendingCar: () => setPendingCarFor(null),
    setPlayer,
    chooseCar,
    saveCar,
    deleteCar,
    dropEmptyCar,
  };
};

export type TravelResponder = ReturnType<typeof useTravelResponder>;
